import type { ShareCardAspect, ShareCardAvatarMode } from './shareCardHelpers';

export type QuizShareType = 'personality' | 'single' | 'bedroom';
export type QuizShareAction = 'shared' | 'downloaded';

const MIN_UPLOAD_BYTES = 8_000;
const UPLOADED_STORAGE_KEY = 'nagiQuizUploadedFingerprints';

export interface SaveQuizShareImageParams {
  blob: Blob;
  quizType: QuizShareType;
  variantKey?: string;
  aspectRatio: ShareCardAspect;
  displayName: string;
  shareTitle: string;
  recordId?: string;
  shareAction: QuizShareAction;
  consentPromo?: boolean;
  avatarMode?: ShareCardAvatarMode;
}

const inFlightUploads = new Set<string>();

function buildUploadFingerprint(
  params: Omit<SaveQuizShareImageParams, 'blob' | 'shareAction' | 'consentPromo'>
): string {
  return [
    params.quizType,
    params.aspectRatio,
    params.variantKey || '',
    params.shareTitle.trim(),
    params.displayName.trim(),
    params.recordId || 'guest',
    params.avatarMode || 'default',
  ].join('::');
}

function readUploadedFingerprints(): Set<string> {
  try {
    const raw = sessionStorage.getItem(UPLOADED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
}

function markFingerprintUploaded(fingerprint: string): void {
  try {
    const uploaded = readUploadedFingerprints();
    uploaded.add(fingerprint);
    sessionStorage.setItem(UPLOADED_STORAGE_KEY, JSON.stringify([...uploaded]));
  } catch {
    // sessionStorage có thể bị chặn
  }
}

export interface SaveQuizShareImageResult {
  saved: boolean;
  imageUrl?: string;
}

/**
 * Lưu ảnh quiz lên server. Mỗi kết quả + tỉ lệ + tên hiển thị chỉ upload 1 lần / phiên.
 */
export async function saveQuizShareImage(
  params: SaveQuizShareImageParams
): Promise<SaveQuizShareImageResult> {
  const fingerprint = buildUploadFingerprint(params);

  if (inFlightUploads.has(fingerprint) || readUploadedFingerprints().has(fingerprint)) {
    return { saved: false };
  }

  if (params.blob.size < MIN_UPLOAD_BYTES) {
    console.warn('Bỏ qua lưu ảnh quiz: file quá nhỏ, có thể capture lỗi.', params.blob.size);
    return { saved: false };
  }

  inFlightUploads.add(fingerprint);

  try {
    const form = new FormData();
    form.append('image', params.blob, 'quiz-share.png');
    form.append('quizType', params.quizType);
    form.append('aspectRatio', params.aspectRatio);
    form.append('shareAction', params.shareAction);
    form.append('displayName', params.displayName);
    form.append('shareTitle', params.shareTitle);
    form.append('consentPromo', params.consentPromo === false ? '0' : '1');

    if (params.variantKey) form.append('variantKey', params.variantKey);
    if (params.recordId) form.append('recordId', params.recordId);

    const response = await fetch('/api/quiz-share', { method: 'POST', body: form });
    const data = (await response.json().catch(() => null)) as {
      error?: string;
      imageUrl?: string;
    } | null;

    if (!response.ok) {
      console.warn('Lưu ảnh quiz thất bại:', data?.error || response.statusText);
      return { saved: false };
    }

    markFingerprintUploaded(fingerprint);
    return { saved: true, imageUrl: data?.imageUrl };
  } catch (err) {
    console.warn('Lưu ảnh quiz thất bại:', err);
    return { saved: false };
  } finally {
    inFlightUploads.delete(fingerprint);
  }
}